import { graphic } from 'echarts'
import ReactEcharts from 'echarts-for-react'
import { floorFormatter } from 'nft/utils'
import { useCallback, useEffect, useMemo, useRef } from 'react'

interface IChartData {
  time: number
  locTime: string
  hourTime: string
  dayTime: string
  value: number | number[]
}

interface IProps {
  data: IChartData[]
  isDay: boolean
  setCurrentIndex?: (data: any) => void
  setIsHovered?: (state: boolean) => void
  chartOptions?: {
    xAxis?: {
      boundaryGap?: boolean
      splitLine?: {
        show: boolean
      }
    }
    yAxis?: {
      splitLine?: {
        show: boolean
      }
    }
  }
  chartColor1?: string
  chartColor0?: string
  chartColor: string
  type: 'bar' | 'line' | 'candlestick'
}

export default function Chart({
  setCurrentIndex,
  setIsHovered,
  data,
  isDay,
  chartOptions,
  type,
  chartColor,
  chartColor0,
  chartColor1,
}: IProps) {
  const echartsRef = useRef<any>()

  const resizeHandler = useCallback(() => {
    const echartsInstance = echartsRef.current?.getEchartsInstance()
    if (echartsInstance) {
      echartsInstance.resize()
    }
  }, [echartsRef])

  useEffect(() => {
    window.addEventListener('resize', resizeHandler)
    return () => {
      window.removeEventListener('resize', resizeHandler)
    }
  }, [resizeHandler])

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        show: true,
        formatter(params: any[]) {
          if (typeof params[0].dataIndex === 'number') {
            setCurrentIndex && setCurrentIndex(params[0].dataIndex)
            setIsHovered && setIsHovered(true)
          }
          return null
        },
      },
      title: {
        show: false,
      },
      grid: {
        left: 43,
        top: 26,
        right: 26,
        bottom: 26,
      },
      xAxis: {
        type: 'category',
        boundaryGap: chartOptions?.xAxis?.boundaryGap ?? true,
        data: data.map(({ time }) => time),
        splitLine: {
          show: chartOptions?.xAxis?.splitLine?.show ?? true,
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.11)',
            type: 'dashed',
          },
        },
        axisLabel: {
          fontSize: 8,
          color: '#bcbaac',
          formatter(time: string, index: number) {
            return isDay ? data[index].hourTime : data[index].dayTime
          },
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#2d2d2d',
          },
        },
      },
      yAxis: {
        scale: true,
        type: 'value',
        splitLine: {
          show: chartOptions?.yAxis?.splitLine?.show ?? true,
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.11)',
            type: 'dashed',
          },
        },
        axisLabel: {
          formatter(value: number) {
            return value ? floorFormatter(value) : value
          },
          fontSize: 8,
          color: '#bcbaac',
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#2d2d2d',
          },
        },
      },
      series: [
        {
          name: 'Test Chart',
          type,
          symbol: 'none',
          itemStyle: {
            color: type === 'candlestick' ? '#1FDACF' : type === 'bar' ? '#C2A676' : chartColor,
            color0: '#C13D11',
            borderColor: type === 'candlestick' ? '#1FDACF' : null,
            borderColor0: '#C13D11',
            borderWidth: 2,
          },
          areaStyle:
            chartColor0 && chartColor1
              ? {
                  color: new graphic.LinearGradient(0, 0, 0, 1, [
                    {
                      offset: 0,
                      color: chartColor0,
                    },
                    {
                      offset: 1,
                      color: chartColor1,
                    },
                  ]),
                }
              : undefined,
          data: data.map(({ value }) => value),
        },
      ],
    }),
    [data, setCurrentIndex, setIsHovered, isDay, type, chartColor, chartColor0, chartColor1, chartOptions]
  )
  return <ReactEcharts lazyUpdate={true} option={option} style={{ width: '100%', height: '400px' }} ref={echartsRef} />
}
